import { dayDurationToMs } from 'lib/datetime/time'
import { get, getWithTimestamp, remove, set } from 'lib/storage/versionedLocalStorageUtils'
import { putNewCheckoutCart } from 'api/cart'
import { toUtmParams } from 'lib/url/utmCodesUtils'
import { slimifyGeo } from 'lib/geo/geoUtils'
import { cartItemsAreReady } from 'lib/checkout/checkoutUtils'
import debounce from 'lodash/debounce'
import config from 'constants/config'
import { INTERACTION_STUDIO_EVENT_ACTION } from 'constants/analytics'
import { touch } from 'api/interactionStudio'
import { globalCheckoutData } from 'analytics/interactionStudioData'
import { last, uniqueBy } from 'lib/array/arrayUtils'
import { getFirstDictionaryValue } from 'lib/object/objectUtils'

const TTL_DURATION = dayDurationToMs(10)

// Redux state
// Increment version when we change the state structure in an incompatible way.
const REDUX_CART_STATE_KEY = 'checkoutCartReduxState'
const REDUX_CART_STATE_VERSION = 9

const REDUX_FORM_STATE_KEY = 'checkoutFormReduxState'
const REDUX_FORM_STATE_VERSION = 2

const REDUX_PAYMENT_STATE_KEY = 'checkoutPaymentReduxState'
const REDUX_PAYMENT_STATE_VERSION = 8

// Snapshot form state can persist and be used to restore form data across multiple checkout sessions
const REDUX_FORM_STATE_SNAPSHOT_KEY = 'checkoutFormStateSnapshot'
const REDUX_FORM_STATE_SNAPSHOT_VERSION = 1
/**
 * @remarks
 * Is this actually needed?
 * ENGX-816
 */
interface FormStateSnapshot {
  timestamp: number,
  formState: App.CheckoutFormState,
}

export function saveCheckoutReduxPaymentState(state: App.CheckoutPaymentState) {
  set(REDUX_PAYMENT_STATE_KEY, REDUX_PAYMENT_STATE_VERSION, state)
}

export function getSavedCheckoutReduxPaymentState(): App.CheckoutPaymentState {
  return get(REDUX_PAYMENT_STATE_KEY, REDUX_PAYMENT_STATE_VERSION)
}

export function saveCheckoutReduxCartState(state: App.CheckoutCartState) {
  set(REDUX_CART_STATE_KEY, REDUX_CART_STATE_VERSION, state)
}

export function getSavedCheckoutReduxCartState() {
  return getWithTimestamp<App.CheckoutCartState>(REDUX_CART_STATE_KEY, REDUX_CART_STATE_VERSION)
}

export function saveCheckoutReduxFormState(state: App.CheckoutFormState) {
  set(REDUX_FORM_STATE_KEY, REDUX_FORM_STATE_VERSION, state)
}

export function getSavedCheckoutReduxFormState(): App.CheckoutFormState {
  return get(REDUX_FORM_STATE_KEY, REDUX_FORM_STATE_VERSION)
}

export function clearFormStateCheckout() {
  remove(REDUX_FORM_STATE_KEY)
}

export function saveCheckoutFormStateSnapshot(state: App.CheckoutFormState) {
  const entry: FormStateSnapshot = { timestamp: Date.now(), formState: state }
  set(REDUX_FORM_STATE_SNAPSHOT_KEY, REDUX_FORM_STATE_SNAPSHOT_VERSION, entry)
}

export function clearFormStateSnapshot() {
  remove(REDUX_FORM_STATE_SNAPSHOT_KEY)
}

export function getSavedCheckoutFormStateSnapshot() {
  const snapshot = get(REDUX_FORM_STATE_SNAPSHOT_KEY, REDUX_FORM_STATE_SNAPSHOT_VERSION) as FormStateSnapshot

  if (snapshot) {
    const { timestamp, formState } = snapshot
    if (Date.now() < timestamp + TTL_DURATION) {
      return formState
    } else {
      clearFormStateSnapshot()
    }
  }
}

/**
 * svc-cart still expects UTM params
 *
 * @remarks
 * Customer portal no longer uses it, so we keep this implementation isolated to where still need it
 */
export function getUtmParams(utm: App.UTMState) {
  return {
    utm_source: config.BRAND,
    utm_medium: 'web',
    ...toUtmParams(utm),
  }
}

export const saveCheckoutInCartServiceWithDebounce = debounce(saveCheckoutInCartService, 500)

async function saveCheckoutInCartService(state: App.State) {
  const cart = state.checkout.cart
  if (cart.items.length > 0 && cartItemsAreReady(cart.items)) {
    const cartData = {
      items: augmentCartItemsFromState(cart.items, state),
      utm_params: getUtmParams(state.utm),
      geo: slimifyGeo(state.geo),
    }
    await putNewCheckoutCart(cart.transactionKey, cartData)
    touch(globalCheckoutData(state), INTERACTION_STUDIO_EVENT_ACTION.ReviewPayment)
  }
}

function augmentCartItemsFromState(items: App.Checkout.Item[], state: App.State) {
  return items.map((item) => {
    if (item.itemType === 'insurance') return augmentInsuranceItemsFromState(item, state)
    if (item.itemType === 'booking-protection') return augmentBookingProtectionItemsFromState(item, state)
    return item
  })
}

function augmentInsuranceItemsFromState(item: App.Checkout.InsuranceItem, state: App.State) {
  if (item.policyIds?.length) {
    const fullProtectionProduct = state.insurance.products.find(p => p.isFullProtection)
    const lastQuoteId = last(state.insurance.quotes)?.id
    const policyQuotes = state.insurance.quotes.filter(q => q.productId !== fullProtectionProduct?.id && q.id === lastQuoteId).map(q => q.policyQuotes).flat()
    item.quotedPrice = uniqueBy(policyQuotes, q => q.policy).reduce((acc, policyQuote) => {
      if (item.policyIds?.includes(policyQuote.policy)) return acc + policyQuote.price
      return acc
    }, 0)
  } else {
    item.quotedPrice = state.insurance.quotes.find((quote) => quote.productId === item.productId)?.total || 0
  }
  return item
}

function augmentBookingProtectionItemsFromState(item: App.Checkout.BookingProtectionItem, state: App.State) {
  if (!state.bookingProtection.quotes) return item
  item.quotedPrice = getFirstDictionaryValue(state.bookingProtection.quotes)?.products.reduce((acc, quote) => {
    if (!quote.productPrice) return acc
    return acc + quote.productPrice
  }, 0) || 0
  return item
}
